// Single source of truth for the dark-navy / cyber-mint palette.
//
// Three surfaces need these same colours and only one of them can use CSS
// classes, so the hex values live here in JS rather than in the stylesheet:
//   1. Tailwind      — via tailwind.config.ts, referenced from styles.css with @config
//   2. CodeMirror    — EditorView.theme() + HighlightStyle (components/Editor.tsx,
//                      components/ContentEditor.tsx) is themed through JS objects
//   3. xterm.js      — the ITheme object in components/Terminal.tsx, likewise JS
//
// Writing a hex in two places is how those three drift apart, so nothing below
// should ever be duplicated into styles.css by hand.
//
// The `dark` values are taken verbatim from the HTML/Tailwind prototype. The
// `light` values are derived: the prototype is dark-only, so light mode mirrors
// its structure (recessed working surface, mint accent, two border weights)
// rather than copying values that do not exist upstream.

/** Every colour role the app themes. Both palettes below must fill all of them. */
export type Palette = {
  /** Guide pane — carries the grid pattern. */
  paneLeft: string;
  /** Editor/terminal pane. The working surface, and the page backdrop. */
  paneRight: string;
  /** Raised card inside the guide pane (topology card). */
  card: string;
  /** Editor header strip, one step off the pane. */
  editorHeader: string;
  /** "YOUR ZONE" highlight fill, and its brighter border. */
  zoneFill: string;
  zoneBorder: string;
  /** Callout / note box fill. */
  note: string;
  /** Primary border, and the softer one used on cards and controls. */
  line: string;
  lineSoft: string;
  /** Scrollbar thumb, and its hover state. */
  thumb: string;
  thumbHover: string;
  /** Headings and emphasised values — `text-white` in the prototype. */
  inkStrong: string;
  /** Body text, secondary text, and gutter line numbers. */
  ink: string;
  inkMuted: string;
  inkFaint: string;
  /** Cyber-mint accent, and the darker step used for hover/press. */
  accent: string;
  accentHover: string;
  /** Text placed on top of a filled accent surface. */
  onAccent: string;
  /** Status colours. Kept in the palette so they stay legible in both themes. */
  ok: string;
  warn: string;
  danger: string;
  orange: string;
};

/** Verbatim from the prototype. Do not "tidy" these values. */
export const dark: Palette = {
  paneLeft: "#171E27",
  paneRight: "#11161D",
  card: "#1A232D",
  editorHeader: "#19212A",
  zoneFill: "#1A3034",
  zoneBorder: "#4AB3A4",
  note: "#173437",
  line: "#2A3644",
  lineSoft: "#364454",
  thumb: "#2A3644",
  thumbHover: "#3B4A5A",
  inkStrong: "#FFFFFF",
  ink: "#D2D8DF",
  inkMuted: "#8B9DAE",
  inkFaint: "#4A5A6A",
  accent: "#55C9B3",
  accentHover: "#4AB3A4",
  onAccent: "#11161D",
  ok: "#58BD8B",
  warn: "#DDB755",
  danger: "#EB7777",
  orange: "#E69255",
};

// Derived, not from the prototype. Two deliberate departures, both about
// contrast rather than taste:
//   - the accent darkens to #0E7C6B. #55C9B3 on white is ~1.9:1, which fails
//     text contrast outright; the mint reads as mint but becomes legible.
//   - paneRight stays the *recessed* surface as it is in dark mode, so the
//     working pane still sits behind the guide rather than in front of it.
export const light: Palette = {
  paneLeft: "#FFFFFF",
  paneRight: "#F1F4F8",
  card: "#F8FAFC",
  editorHeader: "#FFFFFF",
  zoneFill: "#E3F5F1",
  zoneBorder: "#3FA595",
  note: "#E8F6F3",
  line: "#D5DDE6",
  lineSoft: "#C3CEDA",
  thumb: "#C3CEDA",
  thumbHover: "#A9B7C6",
  inkStrong: "#0F172A",
  ink: "#18232D",
  inkMuted: "#5A6B7C",
  inkFaint: "#93A1B0",
  accent: "#0E7C6B",
  accentHover: "#0B6357",
  onAccent: "#FFFFFF",
  ok: "#23845B",
  warn: "#9A6B08",
  danger: "#B13D3D",
  orange: "#C75B12",
};

/** Editor syntax colours — verbatim from the prototype, dark-only by design. */
export const syntax = {
  comment: "#788796",
  keyword: dark.accent,
  string: "#98C379",
} as const;

export const font = {
  /** UI text. Sarabun, not the prototype's Inter: Inter ships no Thai glyphs,
   *  and Thai is the primary language here. Swapping it in would drop every
   *  Thai string to a system fallback face. */
  ui: '"Sarabun", system-ui, sans-serif',
  /** Code, IPs, container names, zone names — the prototype's own mono face. */
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
} as const;

// --- Background patterns -----------------------------------------------------
//
// techGrid is copied byte-for-byte from the prototype's <style> block, then
// parameterised so the same geometry can be tinted for light mode.
//
// It was wrong until the prototype HTML actually arrived: a 40x40 #222E3C tile,
// reconstructed from the phrase "Grid, Dot Matrix" while the source was still
// missing. The real tile is 60x60 stroked #1F2A38 -- a noticeably wider mesh.
// techDots / techDotsDark are STILL reconstructed: the login prototype uses no
// dot pattern, so there is nothing to copy them from yet.
const gridTile = (size: number, stroke: string, opacity: string) =>
  `url("data:image/svg+xml,%3Csvg width='${size}' height='${size}' viewBox='0 0 ${size} ${size}' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M${size} 0L0 0 0 ${size}' fill='none' stroke='%23${stroke.slice(1)}' stroke-opacity='${opacity}' stroke-width='1'/%3E%3C/svg%3E")`;

const dotTile = (size: number, fill: string, opacity: string) =>
  `url("data:image/svg+xml,%3Csvg width='${size}' height='${size}' viewBox='0 0 ${size} ${size}' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='2' cy='2' r='1' fill='%23${fill.slice(1)}' fill-opacity='${opacity}'/%3E%3C/svg%3E")`;

export const pattern = {
  dark: {
    /** Blueprint grid — guide pane background. 60x60, #1F2A38 @ 0.6. */
    techGrid: gridTile(60, "#1F2A38", "0.6"),
    /** Mint dot matrix — highlight and note boxes. 16x16, mint @ 0.15. */
    techDots: dotTile(16, dark.accent, "0.15"),
    /** Grey dot matrix — topology card. 20x20, #364454 @ 0.3. */
    techDotsDark: dotTile(20, "#364454", "0.3"),
  },
  light: {
    techGrid: gridTile(60, "#C9D4E0", "0.9"),
    techDots: dotTile(16, light.accent, "0.13"),
    techDotsDark: dotTile(20, "#B6C2D0", "0.5"),
  },
} as const;

export const tokens = { dark, light, syntax, font, pattern } as const;

export default tokens;
